import React, { useState } from 'react';
import { XMarkIcon, DocumentTextIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

interface PriceList {
  id: string;
  title: string;
  description: string;
  file: string;
  category: 'water' | 'gas' | 'coextruded_water' | 'coextruded_gas';
}

const priceLists: PriceList[] = [
  {
    id: 'price-water-standard',
    title: 'Прайс-лист трубы для водоснабжения',
    description: 'Стандартные полиэтиленовые трубы для систем водоснабжения',
    file: '/assets/price/Прайс лист Утверд Вода от 16.03.26.pdf',
    category: 'water'
  },
  {
    id: 'price-water-coextruded',
    title: 'Прайс-лист соэкструзионные трубы для водоснабжения',
    description: 'Соэкструзионные полиэтиленовые трубы для систем водоснабжения 2026',
    file: '/assets/price/прайс трубы соэкструз Вода 2026.pdf',
    category: 'coextruded_water'
  },
  {
    id: 'price-gas-standard',
    title: 'Прайс-лист трубы для газа',
    description: 'Полиэтиленовые трубы для газоснабжения от 16.03.2026',
    file: '/assets/price/Прайс_лист_трубы_для_газа_16_03_2026_г_.pdf',
    category: 'gas'
  },
  {
    id: 'price-gas-coextruded',
    title: 'Прайс-лист соэкструзионные трубы для газа',
    description: 'Соэкструзионные полиэтиленовые трубы для газоснабжения 2026',
    file: '/assets/price/прайс_лист_трубы_соэкструз_Газ_2026.pdf',
    category: 'coextruded_gas'
  }
];

const categoryData = {
  water: {
    title: 'Водоснабжение',
    icon: DocumentTextIcon,
    bgColor: 'bg-blue-100',
    color: 'text-blue-700'
  },
  gas: {
    title: 'Газоснабжение',
    icon: DocumentTextIcon,
    bgColor: 'bg-red-100',
    color: 'text-red-700'
  },
  coextruded_water: {
    title: 'Соэкструзия Вода',
    icon: DocumentTextIcon,
    bgColor: 'bg-cyan-100',
    color: 'text-cyan-700'
  },
  coextruded_gas: {
    title: 'Соэкструзия Газ',
    icon: DocumentTextIcon,
    bgColor: 'bg-orange-100',
    color: 'text-orange-700'
  }
};

const PriceListPage: React.FC = () => {
  const [selectedPriceList, setSelectedPriceList] = useState<PriceList | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const filteredPriceLists = filter === 'all' 
    ? priceLists 
    : priceLists.filter(list => list.category === filter);

  const openModal = (priceList: PriceList) => {
    setSelectedPriceList(priceList);
  };

  const closeModal = () => {
    setSelectedPriceList(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900">
      {/* Header */}
      <div className="bg-white dark:bg-secondary-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
              💰 Прайс-листы
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Прайс-листы <span className="text-primary-600">ТОО «KazPolimerPlast»</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-secondary-300 max-w-4xl mx-auto">
              Актуальные цены на полиэтиленовые трубы для водоснабжения и газоснабжения
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Статистика */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="text-center bg-white dark:bg-secondary-800 rounded-lg p-6 shadow-sm">
            <div className="text-3xl font-bold text-primary-600">4</div>
            <div className="text-sm text-gray-600 dark:text-secondary-300">Прайс-листа</div>
          </div>
          <div className="text-center bg-white dark:bg-secondary-800 rounded-lg p-6 shadow-sm">
            <div className="text-3xl font-bold text-primary-600">4</div>
            <div className="text-sm text-gray-600 dark:text-secondary-300">Категории</div>
          </div>
          <div className="text-center bg-white dark:bg-secondary-800 rounded-lg p-6 shadow-sm">
            <div className="text-3xl font-bold text-primary-600">2026</div>
            <div className="text-sm text-gray-600 dark:text-secondary-300">Год</div>
          </div>
          <div className="text-center bg-white dark:bg-secondary-800 rounded-lg p-6 shadow-sm">
            <div className="text-3xl font-bold text-primary-600">PDF</div>
            <div className="text-sm text-gray-600 dark:text-secondary-300">Формат</div>
          </div>
        </div>

        {/* Фильтры */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
          >
            Все
          </button>
          <button
            onClick={() => setFilter('water')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'water'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
          >
            Водоснабжение
          </button>
          <button
            onClick={() => setFilter('gas')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'gas'
                ? 'bg-red-600 text-white'
                : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
          >
            Газоснабжение
          </button>
          <button
            onClick={() => setFilter('coextruded_water')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'coextruded_water'
                ? 'bg-cyan-600 text-white'
                : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
          >
            Соэкструзия Вода
          </button>
          <button
            onClick={() => setFilter('coextruded_gas')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'coextruded_gas'
                ? 'bg-orange-600 text-white'
                : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
          >
            Соэкструзия Газ
          </button>
        </div>

        {/* Прайс-листы */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {filteredPriceLists.map((priceList) => {
            const categoryDataItem = categoryData[priceList.category];
            const IconComponent = categoryDataItem.icon;

            return (
              <div
                key={priceList.id}
                className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openModal(priceList)}
              >
                <div className="relative">
                  <div className="aspect-[4/3] bg-gray-100 dark:bg-secondary-700 rounded-t-lg flex items-center justify-center">
                    <object
                      data={priceList.file}
                      type="application/pdf"
                      className="w-full h-full rounded-t-lg"
                    >
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">PDF превью</p>
                        </div>
                      </div>
                    </object>
                  </div>
                  <div className={`absolute top-4 left-4 ${categoryDataItem.bgColor} ${categoryDataItem.color} px-3 py-1 rounded-full text-xs font-medium flex items-center`}>
                    <IconComponent className="w-3 h-3 mr-1" />
                    {categoryDataItem.title}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {priceList.title}
                  </h3>
                  <p className="text-gray-600 dark:text-secondary-300 text-sm mb-4 line-clamp-3">
                    {priceList.description}
                  </p>
                  
                  <div className="mt-4">
                    <span className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium">
                      Посмотреть прайс-лист
                      <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Модальное окно для просмотра PDF */}
        {selectedPriceList && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-secondary-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-secondary-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedPriceList.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-secondary-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                <div className="aspect-[4/3] bg-gray-100 dark:bg-secondary-700 rounded-lg overflow-hidden">
                  <iframe
                    src={selectedPriceList.file}
                    className="w-full h-full"
                    title={selectedPriceList.title}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceListPage;